import React, { useEffect, useState } from "react";
import Select from "react-select";
import { MDBBtn, MDBCheckbox, MDBIcon } from "mdb-react-ui-kit";
import AdminTable from "../../Components/AdminTable/AdminTable";
import Paginator from "../../Components/Paginator/Paginator";
import LoadingIndicator from "../../Components/LoadingIndicator/LoadingIndicator";
import { useNavigate } from "react-router-dom";
import ApiService from "../../Core/Service/ApiService";
import BulkActionPanel, { BulkActions } from "../../Components/CMS/BulkActionPanel";
import { getImageUrl } from "../../Utils/utils";
import { useContext } from "react";
import CmsContext from "../../Context/CmsContext";

const OrganizationList = React.memo(props => {
    const navigate = useNavigate();
    const perPageOptions = [
        {value: 10, label: '10'},
        {value: 20, label: '20'},
        {value: 50, label: '50'},
        {value: 100, label: '100'},
    ]
    const [formData, setFormData] = useState({
        pageNum: 1,
        perPage: 10,
        search: {},
        sort: {}
    })
    const [total, setTotal] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [bulkActioning, setBulkActioning] = useState(false);
    const cmsContext = useContext(CmsContext);

    useEffect(() => {
        cmsContext.setBreadcrumb([
            { title: cmsContext.currMainMenu?.label ?? ''},
            { title: cmsContext.currSubMenu?.label ?? ''},
            { title: 'List'},
        ])
    }, [])

    const getRows = async() => {
        setLoading(true);

        const data = {
            ...formData,
            search: JSON.stringify(formData.search),
            sort: JSON.stringify(formData.sort)
        }

        const response = await ApiService.get('/organizations', data);
        setTotal(response.data.total);
        setRows(response.data.rows);

        setLoading(false);
    }

    useEffect(() => {
        getRows();
    }, [formData])

    const onPageChanged = (pageNum) => {
        setFormData({
            ...formData,
            pageNum
        })
    }
    const onPerPageChanged = (opt) => {
        setFormData({
            ...formData,
            pageNum: 1,
            perPage: opt.value
        })
    }
    const handleSearch = (search) => {
        setFormData({
            ...formData,
            pageNum: 1,
            search: search
        })
    }
    const handleSort = (sort) => {
        setFormData({
            ...formData,
            pageNum: 1,
            sort: sort
        })
    }
    
    const onAddBtnClicked = (e) => {
        navigate(`/organization/create`);
    }
    const gotoEdit = (row) => {
        navigate(`/organization/edit/${row.id}`);
    }
    const handleCopy = (row) => {
        navigate(`/organization/copy/${row.id}`);
    }
    const handleDelete = (row) => {
        if(window.confirm("Are you sure to delete?") == false) return;
        ApiService.delete(`/organizations/${row.id}`).then(() => {
            getRows();
        })
    }
    
    const onAllItemSelected = (selected) => {
        setRows(rows.map(row => ({
            ...row,
            selected: selected
        })))
    }
    const onRowSelected = (e, row) => {
        setRows(rows.map(r => {
            if(r.id != row.id) return r;
            return {
                ...r,
                selected: e.target.checked
            }
        }))
    }

    const handleBulkAction = (bulkData) => {
        if(!bulkData.action) return;
        const selRows = rows?.filter(row => row.selected);
        if(selRows?.length > 0) {
            if(window.confirm('Are you sure to do this action?') == false) return;
            
            setBulkActioning(true);
            const data = {
                ...bulkData,
                ids: selRows.map(r => r.id)
            }
            ApiService.post('/organizations/bulkaction', data).then(() => {
                getRows();
            })
            .finally(() => {
                setBulkActioning(false);
            })
        }
    }

    const renderTbodyTr = (row, rowIndex) => {
        let className = "active";
        return (
            <tr key={rowIndex} className={`${className}`}>
                { columns.map((col, colIndex) => {
                    const el = renderTdCell(row, col.data ?? col.name, rowIndex, colIndex);
                    if(el) {
                        return <td key={colIndex}>{el}</td>
                    } else {
                        return <td key={colIndex}>{row[col.name]}</td>
                    }
                })}
            </tr>
        )
    }

    const renderTdCell = (row, colname, rowIndex, colIndex) => {
        switch(colname) {
            case 'select':
                return <div className="d-flex align-items-center"> 
                    <MDBCheckbox name='' checked={row.selected ?? false} onChange={e => onRowSelected(e, row)} />
                    <span className="mt-1">{row.id}</span>
                </div>
            case 'logo':
                let logo_url = getImageUrl(row.logo_web?.url ?? row.logo_mobile?.url);
                return <img src={logo_url} style={{maxHeight: 50, width: 'auto'}} className="cursor-pointer" onClick={e=>gotoEdit(row)} />
            case 'action':
                return (
                <div className="d-flex justify-content-end">
                    <MDBBtn color="none" tag="a" onClick={() => gotoEdit(row)} title="Edit User">
                        <MDBIcon fas icon="pencil-alt" size="lg" color="warning" />
                    </MDBBtn>
                    <MDBBtn color="none" tag="a" title="Copy Content" onClick={() => handleCopy(row)}>
                        <MDBIcon far icon='copy' size="lg" className="ms-2" color="warning" />
                    </MDBBtn>
                    <MDBBtn color="none" tag="a" onClick={()=>handleDelete(row)} title="Delete Content">
                        <MDBIcon far icon="trash-alt" size="lg" color="danger" className="ms-2" />
                    </MDBBtn>
                </div>
                )
            default:
                return <div className="cursor-pointer" onClick={e=>gotoEdit(row)}>{row[colname]}</div>;
        }
    }

    const columns = [
        {name: 'select', title: 'ID', headerStyle:{width:50, paddingLeft:30}, search: {
            enable: true, type: 'check', onChange: onAllItemSelected
        }},
        {name: 'name', title: 'Name', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }}, 
        {name: 'logo', title: 'Logo' }, 
        {name: 'domain', title: 'Domain', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }}, 
        {name: 'email', title: 'Email', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'telephone', title: 'Telephone', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'address', title: 'Address', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'post_code', title: 'Post Code', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'action', title: 'Action', headerClassName: "d-flex justify-content-end"}
    ];
    return (
    <div className="organization-list mx-auto">
        <div className="d-flex align-items-center me-1 mt-2">
            <div className="flex-fluid d-flex align-items-center ps-2">
                <label>Per Page:</label>
                <div className="ms-2" style={{width:100}}>
                    <Select 
                        options={perPageOptions} 
                        value={perPageOptions.find(opt=>opt.value == formData.perPage)} 
                        onChange={onPerPageChanged}
                    />
                </div>
                <label className="ms-4">
                    Found 
                    <span className='fw-bold mx-2' style={{color: '#333'}}>{total}</span> 
                    Matching Results.
                </label>
            </div>

            <div className="d-flex align-items-center">
                <BulkActionPanel
                    bulkActions={[
                        BulkActions.Delete,
                    ]}
                    handleBulkAction={handleBulkAction} 
                    bulkActioning={bulkActioning} 
                />
                <MDBBtn className="ms-4" onClick={onAddBtnClicked}>
                    <MDBIcon icon="plus" />
                    <span className="ms-1">Create Branch</span>
                </MDBBtn>
            </div>
        </div>

        <div className="flex-fluid mt-3">
            <AdminTable 
                columns={columns} 
                rows={rows} 
                renderTbodyTr={renderTbodyTr} 
                handleSearch={handleSearch} 
                handleSort={handleSort}
                search={formData.search} 
                sort={formData.sort}
            />
        </div>

        <div className="flex-fluid d-flex justify-content-center mt-4">
            <Paginator curPage={formData.pageNum} total={total} perPage={formData.perPage} onPageChanged={onPageChanged} />
        </div>

        <LoadingIndicator show={loading} />
    </div>
    )
});

export default OrganizationList;